import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { delivery } from '../../utils/api';
import { toast } from 'sonner';
import { Truck, MapPin, Phone, Clock, RefreshCw, XCircle, CheckCircle2 } from 'lucide-react';

// Status configuration with colors and labels
const STATUS_CONFIG = {
  created: { 
    label: 'Order Created', 
    color: 'bg-blue-100 text-blue-800',
    icon: Clock,
    description: 'Delivery request submitted to DoorDash'
  },
  confirmed: { 
    label: 'Confirmed', 
    color: 'bg-blue-100 text-blue-800',
    icon: CheckCircle2,
    description: 'DoorDash confirmed the delivery'
  },
  enroute_to_pickup: { 
    label: 'Dasher En Route', 
    color: 'bg-yellow-100 text-yellow-800',
    icon: Truck,
    description: 'Dasher is heading to the store'
  },
  arrived_at_pickup: { 
    label: 'Dasher Arrived', 
    color: 'bg-yellow-100 text-yellow-800',
    icon: MapPin,
    description: 'Dasher arrived at the store'
  },
  picked_up: { 
    label: 'Picked Up', 
    color: 'bg-orange-100 text-orange-800',
    icon: Truck,
    description: 'Order picked up, heading to customer'
  },
  enroute_to_dropoff: { 
    label: 'On The Way', 
    color: 'bg-orange-100 text-orange-800',
    icon: Truck,
    description: 'Dasher is on the way to delivery address'
  },
  arrived_at_dropoff: { 
    label: 'Almost There', 
    color: 'bg-purple-100 text-purple-800',
    icon: MapPin,
    description: 'Dasher arrived at delivery address'
  },
  delivered: { 
    label: 'Delivered', 
    color: 'bg-green-100 text-green-800',
    icon: CheckCircle2,
    description: 'Order successfully delivered!'
  },
  cancelled: { 
    label: 'Cancelled', 
    color: 'bg-red-100 text-red-800',
    icon: XCircle,
    description: 'Delivery was cancelled'
  },
  unknown: { 
    label: 'Unknown', 
    color: 'bg-gray-100 text-gray-800',
    icon: Clock,
    description: 'Status unknown'
  }
};

/**
 * DeliveryTracker Component
 * 
 * Displays real-time delivery status from DoorDash.
 * Polls for status updates every 30 seconds.
 */
export default function DeliveryTracker({ 
  externalDeliveryId, 
  orderId,
  showCancelButton = false,
  onStatusChange,
  compact = false
}) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const fetchStatus = useCallback(async (showRefreshIndicator = false) => {
    if (!externalDeliveryId) return;
    
    if (showRefreshIndicator) setRefreshing(true);
    
    try {
      const response = await delivery.getStatus(externalDeliveryId);
      setStatus(response.data);
      setError(null);
      
      if (onStatusChange) {
        onStatusChange(response.data.delivery_status);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fetch delivery status');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [externalDeliveryId, onStatusChange]);

  useEffect(() => {
    fetchStatus();
    
    // Poll every 30 seconds for status updates
    const interval = setInterval(() => fetchStatus(false), 30000);
    
    return () => clearInterval(interval);
  }, [fetchStatus]);

  const handleCancel = async () => {
    if (!confirm('Are you sure you want to cancel this delivery?')) return;
    
    try {
      await delivery.cancel(externalDeliveryId);
      toast.success('Delivery cancelled successfully');
      fetchStatus(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel delivery');
    }
  };

  const handleRefresh = () => {
    fetchStatus(true);
  };

  if (loading) {
    return (
      <Card className={compact ? "p-3" : ""}>
        <CardContent className={compact ? "p-0" : "p-4"}>
          <div className="flex items-center justify-center py-4">
            <RefreshCw className="w-5 h-5 animate-spin text-gray-400 mr-2" />
            <span className="text-gray-500">Loading delivery status...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={compact ? "p-3" : ""}>
        <CardContent className={compact ? "p-0" : "p-4"}>
          <div className="text-center py-4">
            <p className="text-red-600 text-sm">{error}</p>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleRefresh}
              className="mt-2"
            >
              Retry
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!status) {
    return null;
  }

  const statusConfig = STATUS_CONFIG[status.delivery_status] || STATUS_CONFIG.unknown;
  const StatusIcon = statusConfig.icon;

  // Compact view for order cards
  if (compact) {
    return (
      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
        <div className="flex items-center space-x-2">
          <StatusIcon className="w-4 h-4 text-purple-600" />
          <Badge className={statusConfig.color}>
            {statusConfig.label}
          </Badge>
          {status.dasher_name && (
            <span className="text-sm text-gray-600">• {status.dasher_name}</span>
          )}
        </div>
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={handleRefresh}
          disabled={refreshing}
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
        </Button>
      </div>
    );
  }

  // Full view
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center space-x-2">
            <Truck className="w-5 h-5 text-purple-600" />
            <span>Delivery Tracking</span>
          </CardTitle>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={handleRefresh}
            disabled={refreshing}
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Status Badge */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <StatusIcon className="w-5 h-5 text-gray-600" />
            <Badge className={statusConfig.color}>
              {statusConfig.label}
            </Badge>
          </div>
          {status.is_mock && (
            <Badge variant="outline" className="text-xs">Mock</Badge>
          )}
        </div>
        
        <p className="text-sm text-gray-600">{statusConfig.description}</p>

        {/* Dasher Info */}
        {status.dasher_name && (
          <div className="flex items-center space-x-2 text-sm p-3 bg-gray-50 rounded-lg">
            <div className="flex-1">
              <p className="font-medium">{status.dasher_name}</p>
              <p className="text-gray-500 text-xs">Your Dasher</p>
            </div>
            {status.dasher_phone_number && (
              <a 
                href={`tel:${status.dasher_phone_number}`}
                className="flex items-center space-x-1 text-purple-600 hover:text-purple-700"
              >
                <Phone className="w-4 h-4" />
                <span>Call</span>
              </a>
            )}
          </div>
        )}

        {/* Estimated Times */}
        <div className="grid grid-cols-2 gap-3 text-sm">
          {status.pickup_time_estimated && (
            <div className="p-2 bg-gray-50 rounded">
              <p className="text-gray-500 text-xs">Est. Pickup</p>
              <p className="font-medium">
                {new Date(status.pickup_time_estimated).toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
            </div>
          )}
          {status.dropoff_time_estimated && (
            <div className="p-2 bg-gray-50 rounded">
              <p className="text-gray-500 text-xs">Est. Delivery</p>
              <p className="font-medium">
                {new Date(status.dropoff_time_estimated).toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
            </div>
          )}
        </div>

        {/* Actual Times (if completed) */}
        {(status.pickup_time_actual || status.dropoff_time_actual) && (
          <div className="grid grid-cols-2 gap-3 text-sm">
            {status.pickup_time_actual && (
              <div className="p-2 bg-green-50 rounded">
                <p className="text-green-600 text-xs">Picked Up At</p>
                <p className="font-medium text-green-700">
                  {new Date(status.pickup_time_actual).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
            )}
            {status.dropoff_time_actual && (
              <div className="p-2 bg-green-50 rounded">
                <p className="text-green-600 text-xs">Delivered At</p>
                <p className="font-medium text-green-700">
                  {new Date(status.dropoff_time_actual).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Delivery ID */}
        <div className="text-xs text-gray-400 border-t pt-3">
          Delivery ID: {externalDeliveryId}
        </div>

        {/* Cancel Button */}
        {showCancelButton && 
         !['delivered', 'cancelled', 'picked_up', 'enroute_to_dropoff', 'arrived_at_dropoff'].includes(status.delivery_status) && (
          <Button 
            variant="outline" 
            className="w-full text-red-600 border-red-200 hover:bg-red-50"
            onClick={handleCancel}
          >
            <XCircle className="w-4 h-4 mr-2" />
            Cancel Delivery
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
